import FullNameField from '@/components/field/FullNameField'
import FieldsContact from '@/producers/stores/components/fields/fieldsContact'
import FieldsInfos from '@/producers/stores/components/fields/fieldsInfos'
import FieldsInfosSirene from '@/producers/stores/components/fields/fieldsInfosSirene'
import FieldsLocation from '@/producers/stores/components/fields/fieldsLocation'
import FieldsPresentation from '@/producers/stores/components/fields/fieldsPresentation'
import FieldsPublication from '@/producers/stores/components/fields/fieldsPublication'
import FieldsSell from '@/producers/stores/components/fields/fieldsSell'
import SireneSyncButton from '@/producers/stores/components/SireneSyncButton'
import {ProducerStore} from '@/types'
import {Box, Grid, Typography, Chip} from '@mui/material'
import React, {useEffect} from 'react'
import {
    Show,
    SimpleShowLayout,
    useShowController,
    EditButton,
    TopToolbar,
    TextField,
    DateField,
    FunctionField,
    NumberField
} from 'react-admin'

export default function ProducerStoreShow() {
    const {record, isLoading: loading} = useShowController<ProducerStore>()

    useEffect(() => {
        if (record) {
            document.title = `Établissement : ${record.name}`
        }
    }, [record])

    return (
        <Show
            title={<FullNameField size="32" sx={{margin: '5px 0'}}/>}
            actions={<ShowActions/>}
        >
            <SimpleShowLayout
                sx={{
                    padding: {
                        md: '32px',
                        xs: '16px'
                    }
                }}
            >
                <Typography variant="h4" gutterBottom>Établissement</Typography>
                <Typography variant="h5" gutterBottom>{record?.name}</Typography>

                <Grid container spacing={5}>
                    <Grid item xs={12} md={6} display={'flex'} flexDirection={'column'} gap={3}>
                        <Box>
                            <Typography variant="h6" gutterBottom>Publication</Typography>
                            <FunctionField
                                source="is_published"
                                label="Publié"
                                render={(record: ProducerStore) => (
                                    <Chip
                                        label={record.is_published ? 'Publié' : 'Non publié'}
                                        color={record.is_published ? 'success' : 'default'}
                                        size="small"
                                    />
                                )}
                            />
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom>Informations générales</Typography>
                            <TextField source="name" label="Nom"/>
                            <TextField source="email" label="Email"/>
                            <TextField source="phone" label="Téléphone"/>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom>Vente</Typography>
                            <FunctionField
                                source="is_direct_selling"
                                label="Vente directe"
                                render={(record: ProducerStore) => (
                                    <Chip
                                        label={record.is_direct_selling ? 'Oui' : 'Non'}
                                        color={record.is_direct_selling ? 'success' : 'default'}
                                        size="small"
                                    />
                                )}
                            />
                            <br/>
                            <FunctionField
                                source="is_organic"
                                label="Agriculture biologique"
                                render={(record: ProducerStore) => (
                                    <Chip
                                        label={record.is_organic ? 'Bio' : 'Conventionnel'}
                                        color={record.is_organic ? 'success' : 'default'}
                                        size="small"
                                    />
                                )}
                            />
                            <br/>
                            <FunctionField
                                source="is_vad"
                                label="Vente à distance"
                                render={(record: ProducerStore) => (
                                    <Chip
                                        label={record.is_vad ? 'VAD activée' : 'VAD désactivée'}
                                        color={record.is_vad ? 'success' : 'default'}
                                        size="small"
                                    />
                                )}
                            />
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom>Informations complémentaires</Typography>
                            <TextField source="siret" label="SIRET"/>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6} display={'flex'} flexDirection={'column'} gap={3}>
                        <Box>
                            <Typography variant="h6" gutterBottom>Localisation</Typography>
                            <TextField source="commune_zipcode" label="Code postal"/>
                            {record?.coordinates && (
                                <Box>
                                    <Typography variant="body2">
                                        Coordonnées: {record.coordinates.lat.toFixed(6)}, {record.coordinates.lng.toFixed(6)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6">Informations SIRENE</Typography>
                                <SireneSyncButton size="small" />
                            </Box>
                            <FunctionField
                                source="administrative_status"
                                label="Statut administratif"
                                render={(record: ProducerStore) => {
                                    if (!record.administrative_status) {
                                        return <Typography variant="body2" color="text.secondary">Non renseigné</Typography>;
                                    }
                                    const isActive = record.administrative_status === 'A';
                                    return (
                                        <Chip
                                            label={isActive ? 'Actif (A)' : 'Fermé (F)'}
                                            color={isActive ? 'success' : 'error'}
                                            size="medium"
                                        />
                                    );
                                }}
                            />
                            <TextField source="code_ape" label="Code APE"/>
                            <NumberField source="effectifs" label="Effectifs" options={{ minimumFractionDigits: 0 }}/>
                            <DateField source="cessation_date" label="Date de cessation" showTime={false}/>
                            <DateField source="sirene_last_update" label="Dernière synchronisation SIRENE" showTime={true}/>
                            <Typography variant="caption" color="text.secondary">
                                Ces informations sont synchronisées automatiquement avec l'API SIRENE de l'INSEE
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </SimpleShowLayout>
        </Show>
    )
}

const ShowActions = () => (
    <TopToolbar>
        <SireneSyncButton variant="outlined" size="small" />
        <EditButton/>
    </TopToolbar>
)